import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db, auth } from '@/js/firebase'
import { logger } from '@/services/loggerService'
import { companyService } from '@/services/companyService'
import { deleteBunnyAssetById, uploadCompanyDocument } from '@/services/bunnyUploadService'
import { valuesEqual } from '@/utils/valueCompare'

const PACKAGE_TYPE_LABELS = {
  uvp: 'UVP',
  podcast: 'Podcast',
  standard: 'Standard',
  msa: 'MSA'
}

/**
 * Get display label for a contract based on packageType
 */
function getContractDisplayLabel(contract) {
  const packageType = contract.packageType || (contract.includeUvp ? 'uvp' : 'standard')
  const startDateStr = contract.startDate
    ? new Date(contract.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not set'
  const endDateStr = contract.endDate
    ? new Date(contract.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : (packageType === 'msa' ? 'Ongoing' : 'Not set')

  if (packageType !== 'standard') {
    const label = PACKAGE_TYPE_LABELS[packageType] || packageType
    return `${label} (${startDateStr} - ${endDateStr})`
  }
  return contract.customName || 'Contract'
}

class ContractService {
  constructor() {
    this.collection = 'contracts'
  }
  
  /**
   * Get a single contract by ID
   */
  async getContract(contractId) {
    try {
      const docRef = doc(db, this.collection, contractId)
      const docSnap = await getDoc(docRef)
      
      if (!docSnap.exists()) {
        throw new Error('Contract not found')
      }
      
      const data = docSnap.data()
      return {
        id: docSnap.id,
        ...data,
        // Convert Timestamps to milliseconds for frontend
        startDate: data.startDate?.toMillis ? data.startDate.toMillis() : data.startDate,
        endDate: data.endDate?.toMillis ? data.endDate.toMillis() : data.endDate,
        createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
        updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt
      }
    } catch (error) {
      // "Not found" is expected for stale references - log at debug level
      const isNotFound = error.message?.toLowerCase().includes('not found')
      if (isNotFound) {
        logger.debug('[ContractService] Contract not found', { contractId })
      } else {
        logger.error('[ContractService] Failed to get contract', {
          error: error.message,
          contractId
        })
      }
      throw error
    }
  }
  
  /**
   * Get all contracts for a company
   * @param {string} companyId - The company ID
   * @param {Object} options - Optional filters
   * @param {string} options.type - Filter by contract type ('production' or 'storage')
   * @param {boolean} options.excludeStorage - Exclude storage contracts (default: true for backwards compatibility)
   */
  async getCompanyContracts(companyId, options = {}) {
    try {
      const { type = null, excludeStorage = true } = options

      // Simple query without ordering to avoid index requirement
      const q = query(
        collection(db, this.collection),
        where('companyId', '==', companyId)
      )

      const querySnapshot = await getDocs(q)
      const contracts = []

      querySnapshot.forEach(doc => {
        const data = doc.data()

        // Filter by type if specified
        if (type && data.type !== type) return

        // Exclude storage contracts by default (backwards compatibility)
        if (excludeStorage && data.type === 'storage') return

        contracts.push({
          id: doc.id,
          ...data,
          // Convert Timestamps to milliseconds for frontend
          startDate: data.startDate?.toMillis ? data.startDate.toMillis() : data.startDate,
          endDate: data.endDate?.toMillis ? data.endDate.toMillis() : data.endDate,
          createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
          updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt
        })
      })

      // Sort in memory instead of in query
      contracts.sort((a, b) => {
        const endDateA = a.endDate || 0
        const endDateB = b.endDate || 0
        return endDateB - endDateA // Descending order
      })

      logger.info('[ContractService] Fetched company contracts', {
        companyId,
        count: contracts.length,
        type: type || 'all (excluding storage)'
      })

      return contracts
    } catch (error) {
      logger.error('[ContractService] Failed to get company contracts', {
        error: error.message,
        companyId
      })
      throw error
    }
  }
  
  /**
   * Get active contracts for a company
   * @param {string} companyId - The company ID
   * @param {Object} options - Optional filters
   * @param {boolean} options.excludeStorage - Exclude storage contracts (default: true)
   */
  async getActiveCompanyContracts(companyId, options = {}) {
    try {
      const { excludeStorage = true } = options

      // Get all contracts and filter in memory to avoid complex index
      const q = query(
        collection(db, this.collection),
        where('companyId', '==', companyId)
      )

      const querySnapshot = await getDocs(q)
      const contracts = []

      querySnapshot.forEach(doc => {
        const data = doc.data()

        // Only include active contracts
        if (!data.active) return

        // Exclude storage contracts by default
        if (excludeStorage && data.type === 'storage') return

        contracts.push({
          id: doc.id,
          ...data,
          // Convert Timestamps to milliseconds for frontend
          startDate: data.startDate?.toMillis ? data.startDate.toMillis() : data.startDate,
          endDate: data.endDate?.toMillis ? data.endDate.toMillis() : data.endDate,
          createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
          updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt
        })
      })

      // Sort in memory
      contracts.sort((a, b) => {
        const endDateA = a.endDate || 0
        const endDateB = b.endDate || 0
        return endDateB - endDateA
      })

      return contracts
    } catch (error) {
      logger.error('[ContractService] Failed to get active company contracts', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Get a company's MSA custom pricing override, if any.
   * When multiple active MSA contracts carry custom pricing,
   * the most recent (by start date) wins.
   *
   * @param {string} companyId
   * @returns {Object|null} The msaPricing object, or null when no override applies
   */
  async getMsaPricingForCompany(companyId) {
    if (!companyId) return null

    const contracts = await this.getActiveCompanyContracts(companyId)
    return this.getMsaPricingFromContracts(contracts, companyId)
  }

  /**
   * Pure variant of getMsaPricingForCompany — derive the MSA pricing override
   * from an already-fetched active contract list (avoids a duplicate query
   * when the caller also needs the contracts themselves).
   *
   * @param {Array} contracts - Active contracts for the company
   * @param {string} [companyId] - For logging only
   * @returns {Object|null} The msaPricing object, or null when no override applies
   */
  getMsaPricingFromContracts(contracts, companyId = null) {
    if (!Array.isArray(contracts)) return null

    const msaWithPricing = contracts
      .filter(contract => contract.packageType === 'msa' && contract.msaPricing)
      .sort((a, b) => (b.startDate || 0) - (a.startDate || 0))

    if (msaWithPricing.length > 0) {
      logger.info('[ContractService] Company has MSA custom pricing', {
        companyId,
        contractId: msaWithPricing[0].id
      })
      return msaWithPricing[0].msaPricing
    }

    return null
  }

  /**
   * The agency contracts governing a managed company, fetched server-side.
   *
   * The security rule does permit these reads (a contract listing your company
   * in `linkedCompanyIds`), but Firestore's query analyser cannot prove an
   * `array-contains` filter satisfies a `hasAny(csvClaim)` condition, so the
   * query is rejected before any document is evaluated. The callable reads them
   * with the Admin SDK after checking the caller belongs to the company.
   *
   * @param {string} companyId
   * @returns {Promise<Array>} active, non-storage contracts
   */
  async getContractsLinkedToCompany(companyId) {
    if (!companyId) return []
    try {
      const fn = httpsCallable(getFunctions(), 'getAgencyContracts')
      const { data } = await fn({ companyId })
      const contracts = data?.contracts || []

      logger.info('[ContractService] Agency contracts resolved', {
        companyId,
        count: contracts.length,
        packageTypes: contracts.map(c => c.packageType)
      })
      return contracts
    } catch (error) {
      logger.error(
        `[ContractService] Failed to load agency contracts for ${companyId}: ` +
        `${error.code || 'unknown'} — ${error.message}`
      )
      return []
    }
  }

  /**
   * Whose contracts govern this company's MSA orders?
   *
   * An agency holds ONE MSA covering every company on its roster, so an
   * agency-linked company prices and bills against the agency's contract, never
   * one of its own. The agency's own company record has agencyId null, so its
   * orders resolve to itself and behave like any direct customer.
   *
   * @param {Object} company - company doc (needs id + agencyId)
   * @returns {string|null}
   */
  msaContractOwnerId(company) {
    return company?.agencyId || company?.id || null
  }

  /**
   * Active contracts that govern this company's MSA orders — the agency's when
   * the company is agency-linked, otherwise the company's own.
   *
   * @param {Object} company - company doc (needs id + agencyId)
   * @returns {Promise<Array>}
   */
  async getActiveMsaContractsFor(company) {
    if (!company?.id) return []

    // Not agency-linked: its own contracts are the whole story.
    if (!company.agencyId) {
      return this.getActiveCompanyContracts(company.id)
    }

    // Agency-linked: the agency's agreement governs its orders, but the company
    // may still hold contracts of its own. Merged rather than replaced so the
    // other gating that reads this list (UVP, client media edits) doesn't lose
    // sight of them.
    //
    // The agency's contracts are fetched by `linkedCompanyIds`, NOT by the
    // agency's companyId. A client user has no claim on the agency, so a
    // companyId query is rejected outright — Firestore refuses any query it
    // cannot prove returns only readable documents. Querying the way the rule is
    // written is what makes it legal for them.
    const [own, agency] = await Promise.all([
      this.getActiveCompanyContracts(company.id),
      this.getContractsLinkedToCompany(company.id)
    ])

    const seen = new Set(own.map((c) => c.id))
    const merged = [...own, ...agency.filter((c) => !seen.has(c.id))]

    logger.info('[ContractService] Resolving contracts via agency', {
      companyId: company.id,
      agencyId: company.agencyId,
      own: own.length,
      fromAgency: agency.length
    })
    return merged
  }

  /**
   * Find the company's active MSA contract (most recent), so MSA-order projects
   * can be tied to it. Unlike getMsaPricingFromContracts, this does NOT require
   * msaPricing to be present on the contract.
   * @param {Array} contracts
   * @returns {Object|null} the MSA contract, or null
   */
  getMsaContractFromContracts(contracts) {
    if (!Array.isArray(contracts)) return null
    const msaContracts = contracts
      .filter(contract => contract.packageType === 'msa')
      .sort((a, b) => (b.startDate || 0) - (a.startDate || 0))
    return msaContracts[0] || null
  }

  /**
   * Create a new contract
   */
  async createContract(contractData) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }
      
      const newContract = {
        ...contractData,
        // Convert dates to Firebase Timestamps
        startDate: contractData.startDate ? Timestamp.fromMillis(contractData.startDate) : null,
        endDate: contractData.endDate ? Timestamp.fromMillis(contractData.endDate) : null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      }
      
      const docRef = await addDoc(collection(db, this.collection), newContract)
      
      // If this is set as default, update the company
      if (contractData.isDefault) {
        await this.updateCompanyDefaultContract(contractData.companyId, docRef.id)
      }
      
      logger.info('[ContractService] Contract created', {
        contractId: docRef.id,
        companyId: contractData.companyId
      })

      // Log contract creation to company activity
      const startDateStr = contractData.startDate ? new Date(contractData.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';
      const endDateStr = contractData.endDate ? new Date(contractData.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';
      const contractLabel = getContractDisplayLabel(contractData);
      const metricValue = contractData.totalMetric || 0;
      const metricType = contractData.useDays ? 'days' : 'hours';
      // MSA contracts are open-ended with no usage metric to log
      const activityDetail = contractData.packageType === 'msa'
        ? `Start: ${startDateStr}, Ongoing`
        : `Start: ${startDateStr}, End: ${endDateStr}, ${metricValue} ${metricType}`;

      await companyService.logActivity(
        contractData.companyId,
        'contract_created',
        contractLabel,
        null,
        null,
        activityDetail
      );

      // Return the created contract with proper date format
      const createdDoc = await getDoc(docRef)
      const data = createdDoc.data()

      return {
        id: docRef.id,
        ...data,
        // Convert Timestamps back to milliseconds for frontend
        startDate: data.startDate?.toMillis ? data.startDate.toMillis() : data.startDate,
        endDate: data.endDate?.toMillis ? data.endDate.toMillis() : data.endDate,
        createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
        updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt
      }
    } catch (error) {
      logger.error('[ContractService] Failed to create contract', {
        error: error.message,
        contractData
      })
      throw error
    }
  }
  
  /**
   * Update an existing contract
   */
  async updateContract(contractId, updates) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }
      
      const docRef = doc(db, this.collection, contractId)

      // Get the current contract data before updating
      const currentContract = await this.getContract(contractId);

      const updateData = {
        ...updates,
        // Convert dates to Firebase Timestamps if they're being updated
        startDate: updates.startDate ? Timestamp.fromMillis(updates.startDate) : updates.startDate,
        endDate: updates.endDate ? Timestamp.fromMillis(updates.endDate) : updates.endDate,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      }

      // Remove date fields if they're undefined (not being updated)
      if (updates.startDate === undefined) delete updateData.startDate
      if (updates.endDate === undefined) delete updateData.endDate

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key]
        }
      })

      await updateDoc(docRef, updateData)

      // If updating default status, update company
      if (updates.isDefault) {
        const contract = await this.getContract(contractId)
        await this.updateCompanyDefaultContract(contract.companyId, contractId)
      }

      // Log contract updates to company activity - track ALL field changes
      const contractLabel = getContractDisplayLabel(currentContract);

      // Fields to skip logging (internal/metadata fields)
      const skipFields = ['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'contractPdfUrl', 'contractPdfName', 'contractPdfPath', 'contractPdfAssetId'];

      // Check all fields in the updates object
      for (const field of Object.keys(updates)) {
        // Skip internal fields
        if (skipFields.includes(field)) continue;

        let oldValue = currentContract[field];
        let newValue = updates[field];

        // Skip if new value is undefined
        if (newValue === undefined) continue;

        // msaPricing is a nested object - compare by content and log without raw values
        if (field === 'msaPricing') {
          if (JSON.stringify(oldValue || null) !== JSON.stringify(newValue || null)) {
            await companyService.logActivity(
              currentContract.companyId,
              'contract_updated',
              `${contractLabel} - ${this.formatContractFieldName(field)}`,
              null,
              null,
              newValue ? 'Custom pricing updated' : 'Custom pricing removed'
            );
          }
          continue;
        }

        // For date fields, ensure both are numbers for comparison
        if (field === 'startDate' || field === 'endDate') {
          oldValue = typeof oldValue === 'number' ? oldValue : oldValue?.toMillis?.() || oldValue;
          newValue = typeof newValue === 'number' ? newValue : newValue?.toMillis?.() || newValue;
        }

        // Only log if the value actually changed. valuesEqual covers empty-vs-empty
        // and Timestamp-vs-Date, which a bare !== reports as phantom changes.
        if (!valuesEqual(oldValue, newValue)) {
          // Format field name for display
          const fieldLabel = this.formatContractFieldName(field);

          await companyService.logActivity(
            currentContract.companyId,
            'contract_updated',
            `${contractLabel} - ${fieldLabel}`,
            oldValue,
            newValue,
            null
          );
        }
      }

      logger.info('[ContractService] Contract updated', {
        contractId,
        updates: Object.keys(updates)
      })

      // Return updated contract
      return await this.getContract(contractId)
    } catch (error) {
      logger.error('[ContractService] Failed to update contract', {
        error: error.message,
        contractId,
        updates
      })
      throw error
    }
  }
  
  /**
   * Delete a contract
   */
  async deleteContract(contractId) {
    try {
      // Get the contract data before deleting
      const contract = await this.getContract(contractId);

      const contractLabel = getContractDisplayLabel(contract);

      // Delete pdfText subcollection if it exists (cached AI extraction data)
      const pdfTextRef = doc(db, this.collection, contractId, 'pdfText', 'content')
      await deleteDoc(pdfTextRef).catch(() => {}) // Silent fail if doesn't exist

      const docRef = doc(db, this.collection, contractId)
      await deleteDoc(docRef)

      // Log contract deletion to company activity
      await companyService.logActivity(
        contract.companyId,
        'contract_deleted',
        contractLabel,
        null,
        null,
        null
      );

      logger.info('[ContractService] Contract deleted', { contractId })
    } catch (error) {
      logger.error('[ContractService] Failed to delete contract', {
        error: error.message,
        contractId
      })
      throw error
    }
  }
  
  /**
   * Clear stale defaultContractId from a company when the contract no longer exists
   */
  async clearStaleContractReference(companyId) {
    try {
      const companyRef = doc(db, 'companies', companyId)
      await updateDoc(companyRef, {
        defaultContractId: null,
        updatedAt: serverTimestamp()
      })

      logger.info('[ContractService] Cleared stale contract reference', { companyId })
      return true
    } catch (error) {
      logger.error('[ContractService] Failed to clear stale contract reference', {
        error: error.message,
        companyId
      })
      return false
    }
  }

  /**
   * Update company's default contract
   */
  async updateCompanyDefaultContract(companyId, contractId) {
    try {
      const companyRef = doc(db, 'companies', companyId)
      await updateDoc(companyRef, {
        defaultContractId: contractId,
        updatedAt: serverTimestamp()
      })
      
      // Update all other contracts to not be default
      const q = query(
        collection(db, this.collection),
        where('companyId', '==', companyId),
        where('isDefault', '==', true)
      )
      
      const querySnapshot = await getDocs(q)
      const updatePromises = []
      
      querySnapshot.forEach(doc => {
        if (doc.id !== contractId) {
          updatePromises.push(
            updateDoc(doc.ref, { isDefault: false })
          )
        }
      })
      
      await Promise.all(updatePromises)
      
      logger.info('[ContractService] Company default contract updated', {
        companyId,
        contractId
      })
    } catch (error) {
      logger.error('[ContractService] Failed to update company default contract', {
        error: error.message,
        companyId,
        contractId
      })
      throw error
    }
  }

  /**
   * Get all contracts matching a specific package type
   * @param {string} packageType - The package type to filter by (e.g., 'podcast', 'uvp', 'standard')
   * @returns {Promise<Array>} Matching contracts
   */
  async getContractsByPackageType(packageType) {
    try {
      const q = query(
        collection(db, this.collection),
        where('packageType', '==', packageType)
      )

      const querySnapshot = await getDocs(q)
      const contracts = []

      querySnapshot.forEach(doc => {
        const data = doc.data()
        contracts.push({
          id: doc.id,
          ...data,
          startDate: data.startDate?.toMillis ? data.startDate.toMillis() : data.startDate,
          endDate: data.endDate?.toMillis ? data.endDate.toMillis() : data.endDate,
          createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
          updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt
        })
      })

      logger.info('[ContractService] Fetched contracts by package type', {
        packageType,
        count: contracts.length
      })

      return contracts
    } catch (error) {
      logger.error('[ContractService] Failed to get contracts by package type', {
        error: error.message,
        packageType
      })
      throw error
    }
  }

  /**
   * Format contract field names for display in activity logs
   * @param {string} field - Field name
   * @returns {string} Formatted field label
   */
  formatContractFieldName(field) {
    const fieldMap = {
      customName: 'Contract Name',
      totalMetric: 'Total Metric',
      startDate: 'Start Date',
      endDate: 'End Date',
      active: 'Status',
      packageType: 'Package Type',
      includeStrategy: 'Strategy',
      includeClientMediaEdits: 'Client Media Edits',
      distributionType: 'Distribution Type',
      organicDistribution: 'Organic Distribution',
      paidDistribution: 'Paid Distribution',
      metricType: 'Metric Type',
      useDays: 'Use Days',
      allowHalfDays: 'Allow Half Days',
      autoRenewal: 'Auto Renewal',
      autoRenewalDays: 'Auto Renewal Days',
      isDefault: 'Default Contract',
      msaPricing: 'Custom Pricing',
      notes: 'Notes',
      companyId: 'Company'
    };

    return fieldMap[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
  }
}

// Contract PDFs upload through the shared Bunny path like everything else.
// uploadCompanyDocument picks the route by size: small PDFs POST straight
// through /api/bunny-upload, and anything over the proxy's 32 MiB request cap
// (which large scanned MSAs hit) goes via the Firebase Storage staging hop.
// Contracts used to own that staging logic outright, because they hit the cap
// first and land in companies/{slug}/documents/ rather than in an asset path.
//
// Returns a real `path` (unlike the older Firebase-only version, which forced
// it to null) so deleteContractPdf can actually clean up on delete/replace
// instead of orphaning the file.
export async function uploadContractPdf(file, companyId) {
  try {
    const { path } = await uploadCompanyDocument({
      file, fileName: file.name, companyId,
    })

    logger.info('[ContractService] Contract PDF uploaded to Bunny', {
      companyId, fileName: file.name, path,
    })
    // No URL is stored — `path` is re-signed on read via refreshBunnyUrl, which
    // is what keeps the link working past a signed URL's TTL.
    return { url: null, filename: file.name, path }
  } catch (error) {
    logger.error('[ContractService] Failed to upload contract PDF', {
      error: error.message,
      companyId,
      fileName: file.name,
    })
    throw error
  }
}

// Delete the Bunny file behind a contract PDF.
//  - path:    new path-backed contracts → delete the storage object directly.
//  - assetId: legacy contracts that still have an `assets` doc (pre-fix).
// Legacy Firebase URLs (neither path nor assetId) are intentionally not cleaned.
export async function deleteContractPdf({ path, companyId, assetId } = {}) {
  if (path) {
    try {
      const fn = httpsCallable(getFunctions(), 'deleteBunnyArtifacts')
      await fn({ companyId, storagePath: path })
    } catch (err) {
      logger.warn('[ContractService] deleteBunnyArtifacts failed (non-fatal)', {
        error: err.message, companyId,
      })
    }
  } else if (assetId) {
    await deleteBunnyAssetById(assetId)
  }
}

/**
 * True if any contract in the list is active and grants client media edits.
 * MSA contracts are intentionally INCLUDED — an active MSA with the flag is a
 * paid entitlement. (Unlike ClientCompanyContractsTab, which hides MSA for
 * display purposes only.)
 * @param {Array} contracts - Contract list (active or mixed — inactive are skipped)
 * @returns {boolean}
 */
export function anyActiveContractAllowsClientMediaEdits(contracts) {
  if (!Array.isArray(contracts)) return false
  return contracts.some(
    (contract) => contract?.active && contract?.includeClientMediaEdits === true
  )
}

export const contractService = new ContractService()